import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle2, Target } from 'lucide-react';

export const GeoAioService = () => {
  const whatWeOffer = [
    {
      title: 'Content & Structure',
      items: [
        'Re-structure key pages to be machine-friendly and easy to cite',
        'Create and optimize Q&A, comparison, and "best [X]" style content',
        'Implement schema and structured data for AI comprehension'
      ]
    },
    {
      title: 'Authority & Signals',
      items: [
        'Help you earn mentions, backlinks, and PR coverage on sites AI engines trust',
        'Optimize your reviews, ratings, and third-party profiles',
        'Strengthen your entity so AI models associate your brand with core categories'
      ]
    },
    {
      title: 'Monitoring & Iteration',
      items: [
        'Track where and how your brand appears in AI answers over time',
        'Compare your AI visibility against competitors',
        'Continuously test new formats and double down on what works'
      ]
    }
  ];

  const deliverables = [
    'AI visibility report: Citations in ChatGPT, Perplexity, Google AI Overviews, and other tools',
    'Competitor citation analysis',
    'Content optimization: 2-5 key pages optimized for GEO/AIO (depends on plan)',
    'Authority campaigns: 1-2 PR/authority pushes per month',
    'Prioritized roadmap for next month'
  ];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero opacity-5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 bg-primary/10 text-primary border-primary/20">
                <Target className="w-3 h-3 mr-1" />
                Core Service
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
                GEO / AIO: Get Your Brand{' '}
                <span className="text-gradient">Referenced in AI Answers</span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-8">
                Generative Engine Optimization (GEO) and AI Visibility Optimization (AIO) ensure that when people ask AI tools for solutions, your brand is the one they recommend.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/free-audit">
                  <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
                    Get Free Audit
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1745674684539-d90293d659a9?w=800&h=600&fit=crop"
                alt="GEO/AIO Service"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is GEO/AIO */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6 text-center">
            What is GEO / AIO?
          </h2>
          <p className="text-lg text-foreground leading-relaxed mb-6">
            GEO (Generative Engine Optimization) and AIO (AI Visibility Optimization) are about making your brand a trusted, cited source for AI engines like ChatGPT, Perplexity, Claude, Google's AI Overviews, and others. Instead of only ranking in traditional search, your content becomes part of the answers these models generate.
          </p>
        </div>
      </section>

      {/* Why GEO Matters */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-12 text-center">
            Why GEO Matters Now
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-border">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display font-semibold mb-2">AI Is the New Search</h3>
                    <p className="text-muted-foreground">Millions of people use AI as their primary search tool every day, bypassing traditional search engines.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display font-semibold mb-2">Limited Citations</h3>
                    <p className="text-muted-foreground">AI engines only mention a few sources per answer—being one of them drives visibility and trust.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display font-semibold mb-2">Authority Building</h3>
                    <p className="text-muted-foreground">Once an AI model learns your brand as an authority, it tends to recommend you more often.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display font-semibold mb-2">First-Mover Advantage</h3>
                    <p className="text-muted-foreground">Early movers in GEO are capturing category leadership while competitors focus only on traditional SEO.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-12 text-center">
            Our GEO/AIO Framework
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {whatWeOffer.map((section, index) => (
              <Card key={index} className="border-border">
                <CardContent className="pt-6">
                  <h3 className="font-display font-semibold text-xl mb-4">{section.title}</h3>
                  <ul className="space-y-3">
                    {section.items.map((item, iIndex) => (
                      <li key={iIndex} className="flex items-start space-x-2">
                        <span className="text-primary mt-1">•</span>
                        <span className="text-muted-foreground text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6 text-center">
            Monthly Deliverables
          </h2>
          <p className="text-center text-muted-foreground mb-12">
            What you can expect each month (varies by plan)
          </p>
          <div className="space-y-4">
            {deliverables.map((item, index) => (
              <div key={index} className="flex items-start space-x-4">
                <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-8 text-center">
            GEO / AIO FAQs
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                How is GEO different from traditional SEO?
              </h3>
              <p className="text-muted-foreground">
                SEO focuses on ranking in search engine result pages; GEO focuses on being cited and recommended inside AI answers. We still care about on-page optimization and authority, but we map it to how AI systems choose and summarize sources instead of just blue links.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                How long does it take to see results?
              </h3>
              <p className="text-muted-foreground">
                Most clients start to see early AI citations within 60–90 days of implementation, with more meaningful momentum building after 3–6 months as models refresh and learn your brand as a trusted source.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                Do you work with our existing SEO agency?
              </h3>
              <p className="text-muted-foreground">
                Yes. We can collaborate with your current SEO or content teams to extend their work into GEO/AIO instead of replacing it—sharing research, technical recommendations, and content briefs that support both channels.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                What inputs do you need from our team?
              </h3>
              <p className="text-muted-foreground">
                We&apos;ll ask for access to your existing content, analytics, and key customer insights so we can see how people already talk about your brand and category. From there, we do the heavy lifting on research, strategy, and implementation.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                Will GEO/AIO impact our organic SEO performance?
              </h3>
              <p className="text-muted-foreground">
                In most cases, yes—positively. Many GEO/AIO improvements (better structure, richer content, clearer entities) also help traditional SEO. We design changes so they support both AI visibility and search rankings rather than trading one for the other.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                How do you report on GEO/AIO performance?
              </h3>
              <p className="text-muted-foreground">
                You&apos;ll receive regular reports that show AI citations, example answers where you appear, competitor comparisons, and the impact on traffic, leads, and pipeline. We keep the reporting simple, visual, and tied to real business outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Want to See Your Current AI Visibility?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Get a free GEO/AIO audit that shows exactly where your brand appears (or doesn&apos;t) in AI answers.
          </p>
          <Link to="/free-audit">
            <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
              Get My Free Audit <span className="font-semibold">(Valued at $2,000+)</span>
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
